import type { InsuranceParamsTravelDto, Item } from 'BrokerTsLib/src/dtos'
import { useFormContext } from 'BrokerTsLib/src/rhf'

import { FormLabel } from '@/components/FormLabel'
import { ReactHookFormDatepicker as Datepicker } from '@/containers/forms/Datepicker'
import { FormRow } from '@/containers/forms/FormRow'
import { RadioGroup } from '@/containers/forms/RadioGroup'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useSelections } from '@/hooks'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Grid,
  Radio,
  Select,
  // RadioGroup,
  Stack,
  Text
} from '@chakra-ui/react'
import { Selection, SelectionType } from 'BrokerTsLib/src/enums'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import { Select as ChakraReactSelect } from 'chakra-react-select'
import { addDays, addYears, subDays } from 'date-fns'
import { useMemo, useState } from 'react'

const suggestDates = (args?: { count: number }) => {
  const { count } = args ?? { count: 5 }
  const today = new Date()

  return Array.from({ length: count }).map((_, index) => {
    const date = new Date(today)
    date.setDate(date.getDate() + index + 1)
    return date
  })
}


export const ParametersForm = () => {
  const [tripEndDateOpened, setTripEndDateOpened] = useState(false)

  const { isDisabled } = useInsuranceParams()

  const {
    setValue,
    getValues,
    register,
    watch,
    formState: { errors },
  } = useFormContext<InsuranceParamsTravelDto>()

  const destination = watch('travel.destination')

  const { data: dataDestination, isLoading: isLoadingDestination, isFetching: isFetchingDestination } = useSelections(SelectionType.TravelDestination)
  const { data: dataEuropeDestination, isLoading: isLoadingEuropeDestination, isFetching: isFetchingEuropeDestination } = useSelections(SelectionType.TravelDestEuropeCountry)
  const { data: dataReason, isLoading: isLoadingReason, isFetching: isFetchingReason } = useSelections(SelectionType.TravelReason)
  const { data: dataSportType, isLoading: isLoadingSportType, isFetching: isFetchingSportType } = useSelections(SelectionType.TravelSportType)
  const { data: dataTravelType, isLoading: isLoadingTravelType, isFetching: isFetchingTravelType } = useSelections(SelectionType.TravelType)

  const destinationEuCountryId = watch('travel.destinationEuropeCountry')

  let sortedEuDestinations: Item[] = []
  if (dataEuropeDestination && !isLoadingEuropeDestination && !isFetchingEuropeDestination) {
    sortedEuDestinations = dataEuropeDestination.data
    sortedEuDestinations.sort((a, b) => a.name.localeCompare(b.name))
  }

  let defaultDestination = {
    label: 'Vyberte',
    value: 0
  }

  if (destinationEuCountryId && dataEuropeDestination && !isLoadingEuropeDestination && !isFetchingEuropeDestination) {
    const destData = dataEuropeDestination.data.find(item => item.id == destinationEuCountryId)
    if (destData) {
      defaultDestination = {
        label: destData.name,
        value: destData.id
      }
    }
  }

  const suggestedDates = suggestDates({ count: 5 })

  const handleSuggestDateClick = (date: Date) => {
    if (date)
      handleTripStartDateChange(date)
  }

  const handleTripStartDateChange = (date?: Date) => {
    setValue('generalParams.insuranceStartDate', date)
    const travelType = getValues('travel.travelType')

    const isYearTravel = travelType == Selection.TravelTypeYearly || travelType == Selection.TravelTypeRepeated
    if (isYearTravel) {
      setValue('travel.insuranceEndDate', !date ? undefined : subDays(addYears(date, 1), 1))
    }
    else {
      setValue('travel.insuranceEndDate', !date ? undefined : addDays(date, 7))
      setTripEndDateOpened(true)
    }
  }

  const minDate = watch('generalParams.insuranceStartDate')
  const edOptions = useMemo(() => {
    return {
      minDate: minDate ?? undefined,
      onClose: () => { setTripEndDateOpened(false) }
    }
  }, [minDate])

  return (
    <Grid rowGap={16} w='100%'>
      <FormRow>
        {/* Destinace  - destination */}
        <FormControl isRequired isInvalid={!!errors.travel?.destination}>
          <FormLabel isLoading={isFetchingDestination || isLoadingDestination}>
            Destinace
          </FormLabel>

          <Select
            size="full"
            {...register('travel.destination')}
            value={destination}
            variant='custom'
            isDisabled={isDisabled}
          >
            <option value='' disabled>Vyberte</option>
            {dataDestination?.data.map((item) => (
              <option value={item.id.toString()} key={item.id}>{item.description}</option>
            ))}
          </Select>

          {errors.travel?.destination && <FormErrorMessage>{errors.travel.destination.message}</FormErrorMessage>}

          <FormHelperText>
            Vyberte destinaci klientova pobytu
          </FormHelperText>
        </FormControl>

        {/* Cilova destinace - destinationEuropeCountry*/}
        {destination == Selection.TravelDestinationEurope &&
          <FormControl isRequired isInvalid={!!errors.travel?.destinationEuropeCountry}>

            <FormLabel isLoading={isFetchingEuropeDestination || isLoadingEuropeDestination}>
              Cílová destinace
            </FormLabel>

            {dataEuropeDestination && !isLoadingEuropeDestination && !isFetchingEuropeDestination &&
              <ChakraReactSelect // tento select zda se byt nepouzitelny. Budeme potreboval autocomplete pro dropdowny s 1000+ polozkami a dynamickym dotazovanim (na server nebo do pameti) - napr.: seznam uzivatelu, seznam organizaci, seznam importu v poleas... 
                // https://www.npmjs.com/package/chakra-react-select
                name="travel.destinationEuropeCountry"
                placeholder="Vyberte zemi..."
                isDisabled={isDisabled}
                options={
                  [
                    defaultDestination,
                    ...sortedEuDestinations.map((country) => (
                      {
                        label: country.name,
                        value: country.id
                      }
                    ))]
                }
                chakraStyles={{
                  container: (provided) => ({ ...provided, w: '100%' }),
                  menu: (provided) => ({ ...provided, zIndex: 3, boxShadow: '1px 6px 32px 6px rgba(0,0,0,0.16)' }),
                  dropdownIndicator: (provided) => ({ ...provided, p: 0 })
                }}
                onChange={(value) => {
                  setValue('travel.destinationEuropeCountry', value?.value)
                }}
                defaultValue={defaultDestination}
              />
            }

            <FormHelperText>
              Vyberte destinaci klientova pobytu
            </FormHelperText>

            {errors.travel?.destinationEuropeCountry && <FormErrorMessage>{errors.travel.destinationEuropeCountry.message}</FormErrorMessage>}
          </FormControl>
        }
      </FormRow>

      <FormRow itemsCount={3}>
        {destination && destination != Selection.TravelDestinationCzech &&
          <FormControl isInvalid={!!errors.travel?.reason} isRequired>
            <FormLabel
              tooltip={
                <Box>
                  <strong>POBYTOVÁ CESTA</strong>
                  <Text>Cesta, kterou pojištěný uskutečňuje za účelem odpočinkové a rekreační turistiky, studijní pobyt a stáž, pobyty au pair, brigády studentů.</Text><br />
                  <strong>POZNÁVACÍ CESTA</strong>
                  <Text>Cesta, kterou pojištěný uskutečňuje za účelem odpočinkové, rekreační turistiky nebo rekreačních sportů.</Text><br />
                  <strong>PRACOVNÍ CESTA</strong>
                  <Text>Cesta, kterou pojištěný uskutečňuje za účelem plnění pracovních úkolů nebo v přímé souvislosti s plněním těchto pracovních úkolů zaměstnance vyslaného zaměstnavatelem, fyzické osoby nebo podnikatele (např. administrativní činnosti).</Text><br />
                  <strong>PRACOVNÍ CESTA (manuální)</strong>
                  <Text>Cesta, kterou pojištěný uskutečňuje za účelem plnění pracovních úkolů nebo v přímé souvislosti s plněním těchto pracovních úkolů zaměstnance vyslaného zaměstnavatelem, fyzické osoby nebo podnikatele (např. zednické práce).</Text>
                </Box>
              }
              isLoading={isFetchingReason || isLoadingReason}
            >
              Důvod cesty
            </FormLabel>

            <RadioGroup
              name={'travel.reason'}
              defaultValue={getValues('travel.reason')?.toString() ?? ''}
              isDisabled={isDisabled}
              onChangeValueTransform={parseInt}
            >
              <Stack spacing={4}>
                {dataReason?.data.map((item) => (
                  <Radio value={item.id.toString()} key={item.id}>
                    {item.description}
                  </Radio>
                ))}
              </Stack>
            </RadioGroup>

            {errors.travel?.reason && <FormErrorMessage>{errors.travel.reason.message}</FormErrorMessage>}
          </FormControl>
        }

        <FormControl isInvalid={!!errors.travel?.sportType} isRequired>
          <FormLabel
            tooltip={
              <Box>
                <strong>REKREAČNÍ SPORTY</strong>
                <Text>Běžné individuální nebo kolektivní sporty, např. atletika, badminton, basketbal, cyklistika, fotbal, sjíždění klidné vody (tj. stojatá nebo proudící voda bez peřejí) a sjíždění divoké vody do I. stupně obtížnosti, tenis, turistika v terénech do 3 500m n. m. včetně, vodní a plážové atrakce.</Text><br />
                <strong>ZIMNÍ SPORTY</strong>
                <Text>Např. lední hokej, lyžování a snowboarding na vyznačených a veřejnosti přístupných sjezdovkách a trasách včetně přístupových cest k nim.</Text><br />
                <strong>RIZIKOVÉ SPORTY</strong>
                <Text>Např. bojové sporty, box, cyklokros, jízda na vodním skútru, judo, karate, potápění s použitím samostatných dýchacích přístrojů do hloubky 40 m včetně, rafting nebo jiné sjíždění divoké vody od II. do III. stupně obtížnosti, surfing, vysokohorská turistika v terénech nad 3 500 m n. m..</Text><br />
                <strong>ORGANIZOVANÉ SPORTY</strong>
                <Text>Závody, soutěže pořádané na amatérské úrovni, vč. přípravy na ně. U DIRECT pojišťovny, a.s. a Kooperativa pojišťovny, a.s., VIG se vztahuje i na profesionální sporty.</Text>
              </Box>
            }
            isLoading={isFetchingSportType || isLoadingSportType}
          >
            Provozované sporty
          </FormLabel>

          <RadioGroup
            name={'travel.sportType'}
            onChangeValueTransform={parseInt}
            defaultValue={getValues('travel.sportType')?.toString() ?? ''}
            isDisabled={isDisabled}
          >
            <Stack spacing={4}>
              {dataSportType?.data.map((item) => (
                <Radio value={item.id.toString()} key={item.id}>
                  {item.description}
                </Radio>
              ))}
            </Stack>
          </RadioGroup>

          {errors.travel?.sportType && <FormErrorMessage>{errors.travel.sportType.message}</FormErrorMessage>}
        </FormControl>

        <FormControl isRequired isInvalid={!!errors.travel?.travelType}>
          <FormLabel isLoading={isFetchingTravelType || isLoadingTravelType}>
            Druh cesty
          </FormLabel>

          <RadioGroup
            name={'travel.travelType'}
            onChangeValueTransform={parseInt}
            defaultValue={getValues('travel.travelType')?.toString() ?? ''}
            isDisabled={isDisabled}
          >
            <Stack spacing={4}>
              {dataTravelType?.data.map((item) => (
                <Radio value={item.id.toString()} key={item.id}>
                  {item.description}
                </Radio>
              ))}
            </Stack>
          </RadioGroup>

          {errors.travel?.travelType && <FormErrorMessage>{errors.travel.travelType.message}</FormErrorMessage>}
        </FormControl>
      </FormRow>

      <FormRow
        itemsCount={2}
        sx={{
          gap: {
            base: 0,
            lg: 8
          }
        }}
      >
        <FormControl>
          <FormLabel>Datum počátku pojištění</FormLabel>

          <Datepicker
            name='generalParams.insuranceStartDate'
            isDisabled={isDisabled}
            handleDateChange={handleTripStartDateChange}
            options={{
              minDate: new Date(),
            }}
          />

          <FormControl>
            {!isDisabled &&
              <Stack direction="row" spacing={2}>
                {suggestedDates.map((date) => (
                  <Button
                    key={date.toISOString()}
                    variant="ghost"
                    size="sm"
                    sx={{ h: 10 }}
                    onClick={() => handleSuggestDateClick(date)}
                  >
                    {formatDate(date, 'd.MM.')}
                  </Button>
                ))}
              </Stack>
            }
          </FormControl>
        </FormControl>


        {/* Date end */}
        <FormControl isInvalid={!!errors.travel?.insuranceEndDate} isRequired>
          <FormLabel htmlFor="travel.insuranceEndDate">
            Datum konce pojištění
          </FormLabel>
          <Datepicker
            name='travel.insuranceEndDate'
            isDisabled={isDisabled}
            options={edOptions}
            isOpen={tripEndDateOpened}
          />
        </FormControl>
      </FormRow>
    </Grid>
  )
}